import React, { useEffect, useMemo } from 'react';
import {
  SECTION_ORDER,
  SECTION_LABEL,
  getSectionTooltipContent,
} from './status-description.config';
import {
  parseDescription,
  extractStepNumbers,
} from '../../../../utils/funnels/statuses/status-description.utils';
import { StatusDescriptionSection } from './status-description-block';
import { useFunnelSectionsVisibility } from 'src/modules/settings/hooks/funnels/sections-visibility.hooks';
import { type StatusReference } from '../../../../types/funnels/statuses/status-description.types';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

type FunnelStatusDescriptionBlocksProps = {
  description: string;
  className?: string;
  statusReferences?: StatusReference[];
  statusId?: number | string;
  isAiLinked?: boolean;
};

export const FunnelStatusDescriptionBlocks: React.FC<
  FunnelStatusDescriptionBlocksProps
> = ({
  description,
  className,
  statusReferences,
  statusId,
  isAiLinked = true,
}) => {
  const { t } = useI18n();
  const sections = useMemo(
    () => parseDescription(description || ''),
    [description]
  );
  const validStepNumbers = useMemo(
    () => extractStepNumbers(sections.pasos),
    [sections.pasos]
  );
  const visibilityCtx = useFunnelSectionsVisibility();

  const { bulkAction, bulkNonce, updateSectionExpanded } = visibilityCtx;

  useEffect(() => {
    if (!bulkAction) return;

    SECTION_ORDER.forEach((key) => {
      const uniqueId = (statusId ? `${statusId}:` : 'status:') + key;
      const targetExpanded = bulkAction === 'expand';
      updateSectionExpanded(uniqueId, targetExpanded);
    });
  }, [bulkAction, bulkNonce, sections, statusId, updateSectionExpanded]);

  return (
    <div className={className}>
      <div className="grid grid-cols-1 gap-3">
        {SECTION_ORDER.map((key) => {
          const label = SECTION_LABEL[key];
          const tooltipContent = getSectionTooltipContent(key, t);
          const content = sections[key] || '';
          const hasContent = content.trim().length > 0;
          const sectionUniqueId = (statusId ? `${statusId}:` : 'status:') + key;
          const initiallyExpanded =
            bulkAction === 'collapse'
              ? false
              : bulkAction === 'expand'
                ? true
                : false;
          return (
            <StatusDescriptionSection
              key={key}
              sectionKey={key}
              label={label}
              tooltipContent={tooltipContent}
              content={content}
              hasContent={hasContent}
              sectionUniqueId={sectionUniqueId}
              initiallyExpanded={initiallyExpanded}
              statusReferences={statusReferences}
              validStepNumbers={validStepNumbers}
              isAiLinked={isAiLinked}
            />
          );
        })}
      </div>
    </div>
  );
};
